/**
 * ground_layers — the TWELVE ground material classes of ground material v2
 * (2026-09-25, research report §7 R1; docs/research/2026-09-25-visual-realism/
 * open-world.md §3 R1). Pure: the layer table the cooker, the rules
 * (lib/ground_rules.ts), the workers and the shader (lib/wetness.ts
 * `applyGroundV2`) all read, so the order and the per-layer knobs live in ONE
 * place. The order IS the KTX2 array layer index —
 * tools/asset-pipeline/ground-layers.json is the cooker's copy and
 * `verify:ground-rules` pins the two against each other and against the cooked
 * `client/public/ground/manifest.json`.
 *
 * THE VERTEX LAYOUT (the ≤ 6-buffer law, measured, not guessed): every ground
 * geometry carries its rule outputs as ONE interleaved Float32 buffer of
 * `GROUND_STRIDE` floats — gW0 / gW1 / gW2 (the 12 layer weights, vec4 each),
 * gTint (biome tint rgb + dryness) and gMask (road, field, wet, routeAcross).
 * three keys WebGPU vertex buffers by the underlying buffer, so the five
 * attributes cost ONE buffer: a v2 ground draw binds position + normal + this
 * = 3 of WebGPU's 8 (v1 bound 7: position, normal, uv, biomeWA/WB, roadW,
 * fieldW). The `uv` attribute is GONE in v2: the shader derives a world-planar
 * UV from `positionWorld.xz + uOriginMod` (the origin taken modulo
 * `GROUND_TILE_PERIOD_M` in doubles on the CPU), so a tile never swims at
 * 1,000 km — a raw float32 world coordinate there is 6 cm coarse.
 */
import type { Biome } from '../../../shared/terrain.js';

export const GROUND_LAYER_KEYS = ['loam', 'turf', 'litter', 'moss', 'rock', 'strata', 'gravel', 'beach', 'dune', 'mud', 'snow', 'alien'] as const;
export type GroundLayerKey = typeof GROUND_LAYER_KEYS[number];
export const GROUND_LAYER_COUNT = 12;
export const LAYER_INDEX = Object.fromEntries(GROUND_LAYER_KEYS.map((k, i) => [k, i])) as Record<GroundLayerKey, number>;

export interface GroundLayerParams {
  key: GroundLayerKey;
  /** Near tile (m): one texture repeat — the mid band tiles ×4, the far band ×16. Must divide GROUND_TILE_PERIOD_M / 16. */
  tileM: number;
  /** How far the layer's height channel pokes through its neighbours in the height blend. */
  heightScale: number;
  /** Share of the biome tint the layer takes (0 = its own colour; the alien layer is ALL tint). */
  tintK: number;
  /** Rock: projected along X/Z on steep faces (the strata bands run horizontally). */
  triplanar: boolean;
  /** Parallax-occlusion on this layer: 'high' = high + ultra, 'ultra' = ultra only, 'none'. */
  pom: 'high' | 'ultra' | 'none';
  /** Roughness floor (a scanned grass/moss roughness map reads too glossy under a sun). */
  roughMin: number;
}

export const GROUND_LAYERS: readonly GroundLayerParams[] = [
  { key: 'loam',   tileM: 3.5, heightScale: 0.8,  tintK: 0.8,  triplanar: false, pom: 'ultra', roughMin: 0.7 },
  { key: 'turf',   tileM: 2.8, heightScale: 0.7,  tintK: 0.7,  triplanar: false, pom: 'ultra', roughMin: 0.72 },
  { key: 'litter', tileM: 2.8, heightScale: 0.9,  tintK: 0.4,  triplanar: false, pom: 'ultra', roughMin: 0.7 },
  { key: 'moss',   tileM: 2.8, heightScale: 0.6,  tintK: 0.9,  triplanar: false, pom: 'ultra', roughMin: 0.65 },
  { key: 'rock',   tileM: 5.6, heightScale: 1.0,  tintK: 0.35, triplanar: true,  pom: 'high',  roughMin: 0.55 },
  { key: 'strata', tileM: 8,   heightScale: 1.0,  tintK: 0.4,  triplanar: true,  pom: 'high',  roughMin: 0.6 },
  { key: 'gravel', tileM: 2.8, heightScale: 1.0,  tintK: 0.3,  triplanar: false, pom: 'high',  roughMin: 0.6 },
  { key: 'beach',  tileM: 3.5, heightScale: 0.5,  tintK: 0.35, triplanar: false, pom: 'ultra', roughMin: 0.75 },
  { key: 'dune',   tileM: 7,   heightScale: 0.4,  tintK: 0.5,  triplanar: false, pom: 'ultra', roughMin: 0.75 },
  { key: 'mud',    tileM: 3.5, heightScale: 0.5,  tintK: 0.45, triplanar: false, pom: 'ultra', roughMin: 0.4 },
  { key: 'snow',   tileM: 5.6, heightScale: 0.35, tintK: 0,    triplanar: false, pom: 'none',  roughMin: 0.45 },
  { key: 'alien',  tileM: 4,   heightScale: 0.8,  tintK: 1.0,  triplanar: false, pom: 'ultra', roughMin: 0.7 },
];

/**
 * The period (m) every tile size divides — ×1, ×4 and ×16 (the three distance
 * bands) — so `uOriginMod = origin mod PERIOD` keeps the texture phase exact
 * across an FP64 rebase: 1792 = 2⁸·7 = 3.5·512 = 2.8·640 = 5.6·320 = 4·448 = 7·256 = 8·224.
 * The macro map tiles once per period (≈1.8 km — the note's "1.5 km").
 */
export const GROUND_TILE_PERIOD_M = 1792;
/** The three distance bands' tile multipliers and where they cross-fade (m from the camera). */
export const GROUND_BAND_MUL = [1, 4, 16] as const;
export const GROUND_BAND_FADE: readonly [number, number, number, number] = [18, 36, 110, 170];

/** The potato tier's 8-layer array: logical layer → physical index in the 8-layer set. */
export const POTATO_PHYSICAL: readonly GroundLayerKey[] = ['loam', 'turf', 'moss', 'rock', 'gravel', 'beach', 'mud', 'snow'];
export const POTATO_FOLD: Readonly<Record<GroundLayerKey, GroundLayerKey>> = {
  loam: 'loam', turf: 'turf', litter: 'loam', moss: 'moss', rock: 'rock', strata: 'rock',
  gravel: 'gravel', beach: 'beach', dune: 'beach', mud: 'mud', snow: 'snow', alien: 'loam',
};

/**
 * Mean LINEAR albedo of each cooked layer (after the cook's gains) — the
 * minimal tier's per-vertex colour, the far tier's macro albedo bake and the
 * harness's brightness budget read these; `verify:ground-rules` pins them
 * against `client/public/ground/manifest.json` (facts, the 2048 cook).
 */
export const GROUND_LAYER_MEAN: readonly (readonly [number, number, number])[] = [
  [0.3191, 0.1877, 0.0878], // loam
  [0.2706, 0.2523, 0.0940], // turf (gain 0.8)
  [0.2429, 0.1308, 0.0580], // litter
  [0.0761, 0.1419, 0.0116], // moss
  [0.1539, 0.1161, 0.0597], // rock (gain 1.45)
  [0.2347, 0.1014, 0.0378], // strata (gain 1.1)
  [0.1998, 0.1759, 0.1390], // gravel
  [0.2278, 0.1746, 0.1105], // beach
  [0.2829, 0.2372, 0.1959], // dune
  [0.0922, 0.0728, 0.0484], // mud (gain 1.15)
  [0.7207, 0.7197, 0.7388], // snow (gain 1.9)
  [0.2325, 0.2068, 0.1729], // alien
];

/**
 * §2.5 POROSITY of each layer, 0..1 (surfaces, 2026-09-27; Lagarde, "Water drop 3a — physically based wet surfaces",
 * 2013; Jensen et al. 1999): a PORous dielectric soaks water into its pores, where light enters and leaves through the
 * film's total internal reflection, so its diffuse albedo DARKENS by the porosity (wet sand ≈ ½ its dry value) and it
 * glosses only once saturated; a smooth non-porous surface (rock) barely darkens but gets a water FILM at once (its
 * roughness drops toward 0.1–0.2). Specular F0 is unchanged either way (water's 0.02 ≈ a dielectric's 0.04). The rain
 * wetness and the sea's wet sand band read this per texel (lib/wetness.ts `groundWetDarkening` / `groundWetRoughness`).
 */
export const GROUND_LAYER_POROSITY: readonly number[] = [
  0.75, // loam — soil; the calibration point (×0.55 fully wet, the pre-porosity global)
  0.7, // turf — the root mat under blades
  0.7, // litter — leaves and needles soak and darken
  0.8, // moss — a sponge
  0.2, // rock — dense stone: a film, little darkening
  0.45, // strata — sandstone bands (porous rock)
  0.5, // gravel — grains, open voids
  0.9, // beach — sand (Jensen: wet ≈ ½)
  0.9, // dune — sand
  0.6, // mud — already wet-dark; the film glosses it
  0.3, // snow — wet snow greys a little
  0.5, // alien — grit
];

/** Mean roughness of each cooked layer (B.b) — the far bake's roughness and the harness's gloss budget. */
export const GROUND_LAYER_ROUGH: readonly number[] = [0.812, 0.463, 0.759, 0.609, 0.812, 0.861, 0.811, 0.963, 0.61, 0.967, 0.801, 0.96];

/**
 * THE LOOK SATURATION of each layer (1 = the scan as cooked), applied luminance-preserving BEFORE the biome tint —
 * in the near shader per texel and to the means the minimal tier and the far bake read (desaturating a mean ≡ the
 * mean of the desaturated texels, so near and far agree). Session 2 (2026-09-25), LOOKED AT in every probe shot:
 * the loam scan (`dry_ground_rocks`, mean R/B 3.6) painted every biome's base terracotta — the hub's regolith and
 * a wetland bank alike — so the biome tints (±15 %) could no longer carry Cosmos's identity (research R1: "keep
 * identity by tinting"). Loam → a warm neutral soil (R/B ≈ 1.8); litter and strata keep their warmth, less of it.
 */
export const GROUND_LAYER_SAT: readonly number[] = [0.5, 1, 0.7, 1, 1, 0.75, 1, 1, 1, 1, 1, 1];
/** Rec. 709 luminance of a linear colour (the weights the shader uses). */
export const lumOf = (c: readonly [number, number, number] | readonly number[]): number => 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
/** The layer means AFTER the look saturation — what the minimal tier and the far bake shade with. */
export const GROUND_LAYER_LOOK_MEAN: readonly (readonly [number, number, number])[] = GROUND_LAYER_MEAN.map((m, i) => {
  const l = lumOf(m), s = GROUND_LAYER_SAT[i];
  return [l + (m[0] - l) * s, l + (m[1] - l) * s, l + (m[2] - l) * s] as const;
});

/** Biome tints (linear multipliers, the Cosmos identity — tint, never baked alien colour). */
export const BIOME_TINT: Readonly<Record<Biome, readonly [number, number, number]>> = {
  regolith_plains: [1.0, 0.97, 0.92],
  crystal_forest: [0.74, 1.0, 1.0],
  dune_desert: [1.12, 1.0, 0.8],
  rocky_highlands: [0.96, 0.96, 1.0],
  moss_wetland: [0.84, 1.05, 0.82],
  fungal_basin: [1.04, 0.8, 1.08],
};

// ── the vertex layout ──────────────────────────────────────────────────────
/** Floats per vertex in the interleaved ground buffer. */
export const GROUND_STRIDE = 20;
/** Attribute name → float offset inside the stride (every one a vec4). */
export const GROUND_ATTRS: readonly { name: 'gW0' | 'gW1' | 'gW2' | 'gTint' | 'gMask'; offset: number }[] = [
  { name: 'gW0', offset: 0 },   // loam turf litter moss
  { name: 'gW1', offset: 4 },   // rock strata gravel beach
  { name: 'gW2', offset: 8 },   // dune mud snow alien
  { name: 'gTint', offset: 12 }, // tint r g b, dryness
  { name: 'gMask', offset: 16 }, // road, field, wet, route-across (m, ±8; 8 = none)
];
/** Offsets of the tint and mask vec4s (used by the rule writer). */
export const OFF_TINT = 12;
export const OFF_MASK = 16;
/** The route-across channel's "no route here" value (m). */
export const ROUTE_NONE = 8;

/** The array rungs, top down (ground, 2026-09-26 — the owner: "the ground v2 textures go up to 8k"): the 4096 and
 *  8192 sets are cooked from the 8K scans; THE UNCOMPRESSED FALLBACK LAW drops one rung at a time. */
export const GROUND_ARRAY_RUNGS = [8192, 4096, 2048, 1024, 512] as const;
export type GroundArrayRes = typeof GROUND_ARRAY_RUNGS[number];
/** The rung under `res` (512 stays 512). */
export function groundRungBelow(res: number): GroundArrayRes {
  for (const r of GROUND_ARRAY_RUNGS) if (r < res) return r;
  return 512;
}
/**
 * Sets published ONE KTX2 PER LAYER (`ground_a_00.ktx2` … `ground_a_11.ktx2`) instead of one array file: the basis
 * transcoder's WASM heap caps at 2 GiB and a 12-layer 8192 level alone inflates to 805 MB next to its ~1 GB file, so
 * the loader transcodes the layers one by one and assembles the array (lib/ground_textures.ts). The cooker writes
 * the same split (tools/asset-pipeline/cook-ground-layers.mjs), `verify:ground-rules` checks it.
 */
export const GROUND_SPLIT_SETS: ReadonlySet<string> = new Set(['8192']);
/** A split set's per-layer file (`ab` = 'a' | 'b'). */
export const groundLayerFile = (set: string, ab: 'a' | 'b', layer: number): string => `/ground/${set}/ground_${ab}_${String(layer).padStart(2, '0')}.ktx2`;
/** Array set name for a tier's knobs (the folder under /ground/). */
export function groundSetName(res: number, potato: boolean): string {
  if (potato) return '512p';
  return res >= 8192 ? '8192' : res >= 4096 ? '4096' : res >= 2048 ? '2048' : res >= 1024 ? '1024' : '512';
}
